// src/platform/PlatformAdminPage.jsx
import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { whitenLogo } from "../lib/logoWhiten";
import OrgDetailPanel from "./OrgDetailPanel";

export default function PlatformAdminPage() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // create org modal state
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  // First/last name and seats are required by POST /platform/orgs — the org
  // admin is provisioned in Keycloak and given a seat in the same request, and
  // neither a nameless identity nor a seatless admin is a valid outcome.
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [seats, setSeats] = useState("1");
  const [creating, setCreating] = useState(false);
  // logo upload state (create modal). logoBlob is the converted white PNG that
  // gets uploaded; logoFile is the untouched original (also uploaded).
  const [logoFile, setLogoFile] = useState(null);
  const [logoBlob, setLogoBlob] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [logoMode, setLogoMode] = useState(null);
  const [logoWarn, setLogoWarn] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  async function loadOrgs() {
    setLoading(true);
    try {
      const res = await fetchWithAuth("/api/v1/platform/orgs");
      if (!res.ok) throw new Error("Failed to load orgs");
      const data = await res.json();
      setOrgs(data.orgs || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrgs();
  }, []);

  // Revoke the previous preview objectURL when it changes / on unmount.
  useEffect(() => {
    return () => { if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl); };
  }, [logoPreviewUrl]);

  async function handleLogoSelect(file) {
    if (!file) return;
    setLogoBusy(true);
    try {
      const { blob, mode, warn } = await whitenLogo(file);
      setLogoFile(file);
      setLogoBlob(blob);
      setLogoMode(mode);
      setLogoWarn(warn);
      setLogoPreviewUrl(URL.createObjectURL(blob)); // previous URL revoked by the effect
    } catch (e) {
      console.error("[logo] conversion failed", e);
      alert("Could not process that image. Try a PNG, SVG, or JPEG.");
    } finally {
      setLogoBusy(false);
    }
  }

  function clearLogo() {
    setLogoFile(null);
    setLogoBlob(null);
    setLogoMode(null);
    setLogoWarn(false);
    setLogoPreviewUrl(null);
  }

  function closeCreateModal() {
    setShowCreate(false);
    setName("");
    setAdminEmail("");
    setAdminFirstName("");
    setAdminLastName("");
    setSeats("1");
    clearLogo();
  }

  // Mirrors the server's own check so the button is dead until the body would
  // be accepted. The server still validates — this only avoids a round trip.
  const seatsInt = Number.parseInt(seats, 10);
  const createReady =
    name.trim() &&
    adminEmail.trim() &&
    adminFirstName.trim() &&
    adminLastName.trim() &&
    Number.isInteger(seatsInt) &&
    seatsInt >= 1;

  async function createOrg() {
    if (!createReady) return;
    setCreating(true);
    try {
      // Create stays JSON — the create tx is untouched. Send exactly the five
      // accepted keys: the endpoint rejects any unexpected field outright.
      const res = await fetchWithAuth("/api/v1/platform/orgs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          admin_email: adminEmail.trim().toLowerCase(),
          admin_first_name: adminFirstName.trim(),
          admin_last_name: adminLastName.trim(),
          seats: seatsInt,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        // `detail` carries the part an operator has to act on — most of all the
        // orphan case, which names the Keycloak user left behind.
        throw new Error(
          [err?.error || "Create failed", err?.detail].filter(Boolean).join(" — ")
        );
      }

      const data = await res.json();
      const orgId = data?.org_id;

      // The org is provisioned at this point. These two are reports about work
      // that happened alongside it, not failures of it.
      if (data?.setup_email_sent === false) {
        alert(
          "Org created, but the setup email did not send. " +
          "Resend the invite from the Org Detail panel."
        );
      }
      const skipped = data?.profile_writes_skipped || [];
      if (skipped.length) {
        alert(
          "Org created. The admin's Keycloak account already existed, so these " +
          "fields were kept as-is rather than overwritten:\n" +
          skipped.map(s => `  • ${s.field}: ${s.existing}`).join("\n")
        );
      }

      // Optional logo upload to the new org id — NON-FATAL. The org already
      // exists; a logo failure just surfaces a notice and the org can get one
      // later from the Org Detail panel.
      if (orgId && logoBlob) {
        try {
          const fd = new FormData();
          fd.append("file", logoBlob, "logo.png");
          if (logoFile) fd.append("original", logoFile, logoFile.name);
          const lr = await fetchWithAuth(
            `/api/v1/platform/orgs/${orgId}/logo`,
            { method: "POST", body: fd } // no Content-Type — browser sets the boundary
          );
          if (!lr.ok) throw new Error("logo upload failed");
        } catch (le) {
          console.error("[logo] upload failed (non-fatal)", le);
          alert("Org created; logo upload failed — you can add it later from Org Detail.");
        }
      }

      closeCreateModal();
      await loadOrgs();
    } catch (e) {
      console.error(e);
      alert(e.message);
    } finally {
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center pt-10">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-400">{error}</div>;
  }

  return (
    <div className="p-6 text-sm text-gray-200 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Platform Administration</h1>
          <p className="text-xs text-gray-400">
            Global organization management
          </p>
        </div>

        <button
          onClick={() => setShowCreate(true)}
          className="
            flex items-center gap-2 px-3 py-1.5 rounded
            bg-accent-action/20 border border-accent-action/60
            text-accent-action hover:bg-accent-action/30 transition
          "
        >
          <Plus size={14} />
          Create Org
        </button>
      </div>

      {/* ORG LIST */}
      <div className="flex-1 border border-bg-border rounded overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-bg-soft text-gray-400">
            <tr>
              <th className="text-left px-3 py-2">Organization</th>
              <th className="text-left px-3 py-2">Users</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Created</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map(o => (
              <tr
                key={o.id}
                onClick={() => setSelectedOrg(o)}
                className="
                  border-t border-bg-border hover:bg-bg-soft/40
                  cursor-pointer
                  "
              >
                <td className="px-3 py-2 font-medium">{o.name}</td>
                <td className="px-3 py-2">{o.user_count}</td>
                <td className="px-3 py-2">
                  <span
                    className={`px-2 py-0.5 rounded border text-xs
                      ${o.status === "active"
                        ? "border-green-500/50 text-green-400"
                        : "border-red-500/50 text-red-400"}
                    `}
                  >
                    {o.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-400">
                  {new Date(o.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* ORG DETAIL */}
        {selectedOrg && (
          <div className="absolute inset-0 z-50 bg-black/40 backdrop-blur-[1px]">
            <OrgDetailPanel
              org={selectedOrg}
              onClose={() => setSelectedOrg(null)}
              onUpdated={async () => {
                await loadOrgs();
                setSelectedOrg(null);
              }}
            />
          </div>
        )}
      </div>

      {/* Create Org Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="w-[420px] bg-bg-soft border border-bg-border rounded p-4 space-y-4">
            <h2 className="text-sm font-semibold">Create Organization</h2>

            <div>
              <label className="block text-xs mb-1 text-gray-400">Name</label>
              <input
                className="w-full bg-bg border border-bg-border rounded px-2 py-1"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-xs mb-1 text-gray-400">
                Org Admin Email
              </label>
              <input
                className="w-full bg-bg border border-bg-border rounded px-2 py-1"
                value={adminEmail}
                onChange={e => setAdminEmail(e.target.value)}
                placeholder="admin@company.com"
              />
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs mb-1 text-gray-400">
                  Admin First Name
                </label>
                <input
                  className="w-full bg-bg border border-bg-border rounded px-2 py-1"
                  value={adminFirstName}
                  onChange={e => setAdminFirstName(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs mb-1 text-gray-400">
                  Admin Last Name
                </label>
                <input
                  className="w-full bg-bg border border-bg-border rounded px-2 py-1"
                  value={adminLastName}
                  onChange={e => setAdminLastName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs mb-1 text-gray-400">
                Analyst Seats
              </label>
              <input
                type="number"
                min={1}
                step={1}
                className="w-full bg-bg border border-bg-border rounded px-2 py-1"
                value={seats}
                onChange={e => setSeats(e.target.value)}
              />
              <p className="text-[10px] text-gray-400 mt-1">
                Seats purchased on the ANALYST tier. The org admin consumes one
                of them.
              </p>
            </div>

            {/* Logo (optional) — converted to white-on-transparent client-side;
                the previewed bytes are the uploaded bytes. Navy swatch is the
                real test (that's where it renders in the app header). */}
            <div>
              <label className="block text-xs mb-1 text-gray-400">Logo (optional)</label>

              {!logoPreviewUrl ? (
                <label
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); handleLogoSelect(e.dataTransfer.files?.[0]); }}
                  className="flex items-center justify-center w-full h-16 rounded border border-dashed
                             border-bg-border text-gray-500 text-xs cursor-pointer text-center px-2
                             hover:border-accent-info/50 hover:text-gray-300 transition"
                >
                  {logoBusy ? "Processing…" : "Drop a logo or click to choose (PNG / SVG / JPEG)"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => handleLogoSelect(e.target.files?.[0])}
                  />
                </label>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <div
                      className="flex-1 flex items-center justify-center h-12 rounded"
                      style={{ background: "#005480" }}
                    >
                      <img src={logoPreviewUrl} alt="logo on navy"
                           className="h-7 w-auto max-w-[160px] object-contain" />
                    </div>
                    <div className="flex-1 flex items-center justify-center h-12 rounded bg-white">
                      <img src={logoPreviewUrl} alt="logo on white"
                           className="h-7 w-auto max-w-[160px] object-contain" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-400">
                      {logoMode === "already-white"
                        ? "Already white — unchanged"
                        : logoMode === "recolored"
                          ? "Recolored to white (alpha preserved)"
                          : "Auto-keyed from solid background"}
                    </span>
                    <button
                      type="button"
                      onClick={clearLogo}
                      className="text-[10px] text-gray-400 hover:text-gray-200 underline"
                    >
                      Remove / choose different
                    </button>
                  </div>
                  {logoWarn && (
                    <p className="text-[10px] text-amber-400/90">
                      Auto-keyed from a solid background — verify the navy preview for
                      halos before creating.
                    </p>
                  )}
                </div>
              )}
            </div>

            <p className="text-xs text-fg-secondary italic">
              The org admin is created in Keycloak, assigned an ANALYST seat, and
              emailed a setup link. No imagery or analytics entitlements are
              granted — do that from the Org Detail panel.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={closeCreateModal}
                className="px-3 py-1 rounded border border-bg-border text-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={createOrg}
                disabled={creating || !createReady}
                className="
                  px-3 py-1 rounded
                  bg-accent-action/20 border border-accent-action/60
                  text-accent-action
                  disabled:opacity-40 disabled:cursor-not-allowed
                "
              >
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}